import { Tooltip } from "@mui/material"
import { ContextMenuItem } from "lib/@components/context-menu-bar/context-menu-item"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useDialog } from "lib/@hooks/useDialog"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { Secure } from "lib/authorization/secure"
import { If, Else } from "lib/@components/switch"
import { AUTHORING_MANAGE_LEGISLATION_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { AuthoringDeleteDialog } from "authoring/routes/project/plugins/project-item/authoring-delete-dialog"
import { removeAuthoringLegislation } from "../controller/remove-authoring-legislation"
import { removeAuthoringLegislationGroup } from "../controller/remove-authoring-legislation-group"
import {
    LEGISLATION_FOLDER,
    isFolderNonEmpty,
    folderHasOrphanedLegislation,
    folderDeleteWouldRemoveProtectedLegislation,
} from "../legislation-library-helpers"

export function LegislationLibraryDeleteMenuItem({ item }) {
    const { library } = useBoundContext()
    const isFolder = item?.type === LEGISLATION_FOLDER

    const folderIsProtected = isFolder && folderDeleteWouldRemoveProtectedLegislation(library.state, item?.id)
    const legislationIsProtected = !isFolder && (item?.scheduleCount ?? 0) > 0
    const isProtected = folderIsProtected || legislationIsProtected

    const folderHasContents = isFolder && isFolderNonEmpty(library.state, item?.id)
    const folderHasOrphans = isFolder && folderHasOrphanedLegislation(library.state, item?.id)

    const disabledTooltip = folderIsProtected
        ? "Cannot delete: this folder holds legislation used in published schedules and filed nowhere else."
        : legislationIsProtected
          ? "Cannot delete while used in published schedules."
          : ""

    const deleteMessage = !isFolder
        ? "This legislation will be permanently removed."
        : folderHasOrphans
          ? "Deleting this folder will also permanently delete the legislation filed only inside it. Legislation that also appears in other folders will remain."
          : folderHasContents
            ? "This folder and all its contents will be permanently removed."
            : "This folder will be permanently removed."

    const openDeleteDialog = useDialog(
        <AuthoringDeleteDialog
            value={item?.id}
            title={isFolder ? "Delete this folder?" : "Delete this legislation?"}
            message={deleteMessage}
        />
    )

    return (
        <Secure demand={AUTHORING_MANAGE_LEGISLATION_DEMAND} fallback={<></>}>
            <If value={isProtected}>
                <Tooltip title={disabledTooltip} PopperProps={{ style: { zIndex: 2002 } }}>
                    <span>
                        <ContextMenuItem
                            icon="gravity-ui:trash-bin"
                            label="Delete"
                            disabled
                            divider
                            priority={20}
                            data-cy="legislation-library-delete-menu-item"
                        />
                    </span>
                </Tooltip>
                <Else>
                    <ContextMenuItem
                        icon="gravity-ui:trash-bin"
                        label="Delete"
                        onClick={handleDelete}
                        divider
                        priority={20}
                        data-cy="legislation-library-delete-menu-item"
                    />
                </Else>
            </If>
        </Secure>
    )

    async function handleDelete() {
        try {
            const confirmed = await openDeleteDialog()
            if (!confirmed) return

            if (isFolder) {
                const result = await removeAuthoringLegislationGroup({ id: item.id, force: folderHasContents })
                if (result) library.removeFolder(result)
            } else {
                const deleted = await removeAuthoringLegislation({ id: item.id })
                if (deleted) library.removeLegislation(item.id)
            }
            successSnackbar(isFolder ? "Folder deleted" : "Legislation deleted")
        } catch (e) {
            console.error(e)
            errorSnackbar(isFolder ? "Failed to delete folder" : "Failed to delete legislation")
        }
    }
}
